import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Fingerprint, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

function Welcome() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  useEffect(() => {
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    checkBiometricAvailability();

    const savedCredentials = localStorage.getItem('ajel_credentials');
    if (savedCredentials) {
      const { phone: savedPhone, password: savedPassword } = JSON.parse(savedCredentials);
      setPhone(savedPhone);
      setPassword(savedPassword);
      setRememberMe(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      if ('credentials' in navigator && window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } else {
        console.log('WebAuthn not supported on this device');
        setBiometricAvailable(false);
      }
    } catch (err) {
      console.error('Error checking biometric availability:', err);
      setBiometricAvailable(false);
    }
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('Attempting login for phone:', phone);

      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: `${phone}@ajel.life`,
        password,
      });

      if (signInError) {
        console.error('Sign in error:', signInError);
        throw signInError;
      }
      if (!authData.user) throw new Error('لم يتم العثور على المستخدم');

      console.log('Authentication successful, fetching profile...');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select()
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile error:', profileError);
        throw profileError;
      }

      if (!profile) {
        await supabase.auth.signOut();
        setError('الحساب غير موجود. الرجاء التسجيل أولاً');
        return;
      }

      console.log('Profile found, login successful');

      if (rememberMe) {
        localStorage.setItem('ajel_credentials', JSON.stringify({ phone, password }));
      } else {
        localStorage.removeItem('ajel_credentials');
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Sign in error:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('فشل في الاتصال بالخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.');
        } else if (err.message.includes('Invalid login credentials')) {
          setError('رقم الهاتف أو كلمة المرور غير صحيحة');
        } else {
          setError(err.message);
        }
      } else {
        setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricAuth = async () => {
    try {
      if (!biometricAvailable) {
        alert('جهازك لا يدعم المصادقة البيومترية');
        return;
      }

      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [],
        userVerification: 'required',
        timeout: 60000
      };

      try {
        const assertion = await navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions
        });

        if (assertion) {
          navigate('/home', { replace: true });
        }
      } catch (err) {
        console.error('Biometric authentication failed:', err);
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError') {
            alert('تم إلغاء المصادقة البيومترية من قبل المستخدم');
          } else if (err.name === 'SecurityError') {
            alert('فشلت المصادقة البيومترية لأسباب أمنية');
          } else {
            alert('فشلت المصادقة البيومترية. الرجاء المحاولة مرة أخرى أو استخدام كلمة المرور');
          }
        } else {
          alert('فشلت المصادقة البيومترية. الرجاء المحاولة مرة أخرى أو استخدام كلمة المرور');
        }
      }
    } catch (error) {
      console.error('Biometric setup error:', error);
      alert('حدث خطأ في إعداد المصادقة البيومترية. الرجاء المحاولة مرة أخرى');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#F87171] mb-2">آجل</h1>
            <div className="flex items-center justify-center space-x-2">
              <p className="text-gray-600">دفتر حسابات اجل</p>
              <Tooltip text="تطبيق لإدارة الحسابات والديون بطريقة سهلة وآمنة" />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleLogin}>
            <div className="relative flex items-center">
              <input
                type="tel"
                placeholder="أدخل رقم الهاتف"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12"
                required
              />
              <div className="absolute left-4">
                <Tooltip text="أدخل رقم الهاتف المسجل في حسابك" position="right" />
              </div>
            </div>

            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4"
              >
                {showPassword ? <EyeOff size={20} className="text-gray-400" /> : <Eye size={20} className="text-gray-400" />}
              </button>
              <div className="absolute left-4">
                <Tooltip text="أدخل كلمة المرور الخاصة بحسابك" position="right" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">تذكرني</span>
              </label>
              <button 
                type="button"
                onClick={() => navigate('/reset-password')}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                استعادة كلمة المرور
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">أو المتابعة باستخدام</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <button className="flex items-center justify-center p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png" alt="Google" className="h-5 object-contain" />
              </button>
              <button 
                onClick={handleBiometricAuth}
                disabled={!biometricAvailable}
                className={`flex items-center justify-center p-3 border border-gray-200 rounded-xl ${
                  biometricAvailable ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                }`}
                title={biometricAvailable ? 'استخدم المصادقة البيومترية' : 'المصادقة البيومترية غير متوفرة على هذا الجهاز'}
              >
                <Fingerprint className="h-5 w-5 text-gray-600" />
              </button>
              <button className="flex items-center justify-center p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Facebook_Logo_%282019%29.png/1024px-Facebook_Logo_%282019%29.png" alt="Facebook" className="h-5 object-contain" />
              </button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">
            ليس لديك حساب؟{' '}
            <button 
              onClick={() => navigate('/register')} 
              className="text-blue-500 hover:text-blue-600"
            >
              سجل الآن
            </button>
          </p>

          {!isStandalone && deferredPrompt && (
            <div className="mt-6">
              <button
                onClick={handleInstall}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
              >
                <Smartphone className="w-5 h-5" />
                <span>تثبيت التطبيق</span>
              </button>
              <p className="mt-2 text-xs text-center text-gray-500">احصل على تجربة أفضل مع تطبيق آجل</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Welcome;